import { IUserCustomExcelInfoGroup, defaultFormatFirmOrderExcelInfo, sampleCustomFirmOrderExcelInfo } from '@/coreDomainsModule/file';
import { ExcelOutputResult, ExcelOutputRow, ExcelParseOption } from '@/coreDomainsModule/translateExcel';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  addFileNameToOutputExcelOption,
  defaultExportExcelPageNames,
  defaultOutputExcelPageNames,
} from '@/coreDomainsModule/excelParseOptions';
import { getBusinessManager } from '@/businessRepository/organizationTable/businessManage';
import { simpleCopy } from '@/utils/deepCopy';
import { fm } from '@/appLogic/fileManager/baseFileManager';
import { ExcelExportResult, columnNameWithBusinessKeyRepresentMap } from '@/coreDomainsModule/exportSupplierForecastExcel';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IDate } from '@/coreDomainsModule/date';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { ExportExcelParseOption } from '@/appLogic/interfaces/exportExcel';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { convertBufferToWorkSheet } from '../translate/excelUtils';
import { TranslateOutputFileManager } from '../translate/translateOutputFileManage';
import { i18n } from '@/i18n';
import { OutputOrderData } from '@/orderTableModule/domains/entities/orderResponse';
import { loadMyPropertyPreferences, myPropertyPreferences } from '@/appLogic/view/preferences';

const t = i18n.t;
/**
 * エクセルファイル出力に関係する処理をまとめる
 */
class ExportFileManager {
  private static EXCEL_FILE_EXTENSION = ['xlsx', 'xlsm', 'xlsb', 'xls', 'xla'];

  // 在庫表エクセルを受け取り、「在庫」「内示」「販売実績」「入荷」エクセルの情報を転記する
  public static exportExcel = async (businesses: IBusiness[], file?: File): Promise<void> => {
    if (!file) return;
    await loadMyPropertyPreferences();
    const f = file;
    // 転記エクセルを読み取るためのオプション
    const outputExcelPageNames: [[string, string], ExcelParseOption][] = addFileNameToOutputExcelOption(
      file.name,
      defaultOutputExcelPageNames
    );
    const dateAttr = [
      'receiveOrderDate', // 受注日
      'fixOrderDate', // 発注日
      'crossDate', // 推奨納入日 or 納入日
      'createDate', // 作成日 (エクセル出力日)
      'expectedShippingDate', // 出荷予定日
    ] as const;
    const nestAttr = [
      'quantity', // 個数 = 発注数量÷容量
      'amount', // 推奨発注数量 or 確定発注数量
    ];
    // const outputExcelPageNames: string[] = ['ﾃﾞﾙﾍﾟｯﾄ'];

    // xlsmの方で一旦解析をしておいてそれを用いてXlsxPopulateで貼り付ける方針で
    const data = await fm.convertFileToBuffer(f);
    const rowExcelData = XLSX.read(data, { type: 'array' });
    const workbook: any = await convertBufferToWorkSheet(data);
    // const translateErrorLog: string[][] = [];

    // まずXlsxPopulate出力用のデータを作成する
    for (const pageInfo of outputExcelPageNames) {
      // 転記先のシートが存在しない場合、読み飛ばす
      if (!rowExcelData.Sheets[pageInfo[0][0]]) continue;
      const sheetName: string = pageInfo[0][0];
      const sheet = rowExcelData.Sheets[sheetName];
      const output: ExcelOutputResult = {};
      // キー列名_列名のオブジェクトの配列、オブジェクトの情報として、セルに入力されているvalueやセルアドレスが含まれている
      // const outputExcelRowInfo: ExcelOutputRow[] = FileManager.parseOutputExcelObjects(sheet, pageInfo[0][1], '在庫表', pageInfo[1]);
      // 取引情報から引っ張ってくる
      businesses.forEach((business, index) => {
        // 列名情報のみ欲しいので、1列目のみ情報を取る
        // const info = outputExcelRowInfo[0];
        // とりあえず、セル番号とプロパティを組み合わせたオブジェクトを直代入
        // const userSettingRowInfo = sampleOutputFirmOrderExcelInfo; // デフォルト出力用の情報
        const userSettingRowInfo = sampleCustomFirmOrderExcelInfo; // カスタム出力(一旦長瀬産業書式)用の情報
        Object.keys(userSettingRowInfo).forEach((key) => {
          // TODO: ここ「列名_列名」になるのおかしくない？
          // if (!Object.keys(info).includes(`${option[key].rowName}_${option[key].rowName}`)) return;
          if (userSettingRowInfo[key].isDisplay) {
            // 入力部でvalidateしているのでここで考慮する必要はない（はず？）
            const cellCol = userSettingRowInfo[key].displayCell.replace(/[^a-z]/gi, '');
            const cellRow = parseInt(userSettingRowInfo[key].displayCell.replace(/[^0-9]/g, ''), 10) + index;
            const outputCellNumber = cellCol + cellRow;
            let inputValue = business[key] ?? '-';
            dateAttr.forEach((ele) => {
              if (key === ele) {
                inputValue = outputDateInfoForRecommend(key, business.recommend[0]);
              }
            });
            if (key === 'businessMemo') {
              const valueOj = business[key];
              if (valueOj !== undefined) {
                const valueKey: string = userSettingRowInfo[key].colName;
                inputValue = valueOj[valueKey];
              }
            }
            nestAttr.forEach((ele) => {
              if (key === ele) {
                const recommend = business.recommend[0];
                // / TODO: モーダル出すなどのユーザーに通知する機能の作成が必要だが、一旦データ入力させないようにしている
                if (recommend.year === -1 && recommend.month === -1 && recommend.day === -1) return '0';
                if (key === 'quantity') {
                  const quantityValue: number = recommend.amount / Number(business.package);
                  inputValue = quantityValue;
                } else {
                  inputValue = recommend[key];
                }
              }
            });
            output[outputCellNumber] = {
              address: outputCellNumber,
              value: inputValue,
              prevValue: '',
              // ここ以下は変更履歴作成に使用するために追加
              columnName: userSettingRowInfo[key].colName,
              transactionInfo: undefined,
              isDuplicatedBusiness: false,
            };
          }
        });
      });
      const worksheet = workbook.sheet(sheetName);
      for (const key of Object.keys(output)) {
        worksheet.cell(key)?.value(output[key].value ?? '-');
      }
      const wbout = await workbook.outputAsync();
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      // 日付情報の作成
      saveAs(blob, `${t('確定発注出力', { date: DateService.getTodayString() })}.xlsx`);
    }
  };

  // NOTE: 確定発注エクセル出力を行う処理だが、現在は使用していない
  // ただし、長瀬書式のエクセル出力処理が含まれているため、コード自体は残しておく
  public static exportOrderExcel = async (orders: OutputOrderData[], file?: File): Promise<void | Error> => {
    if (!file) return new Error('ファイルが選択されていません');
    // ファイルの形式はエクセルファイルのみ許可する
    if (!this.EXCEL_FILE_EXTENSION.includes(file.name.split('.').pop() as string)) return new Error('エクセルファイルを選択してください');
    await loadMyPropertyPreferences();

    // 転記エクセルを読み取るためのオプション
    const outputExcelPageNames: [[string, string], ExcelParseOption][] = addFileNameToOutputExcelOption(
      file.name,
      defaultOutputExcelPageNames
    );

    // xlsmの方で一旦解析をしておいてそれを用いてXlsxPopulateで貼り付ける方針で
    const data = await fm.convertFileToBuffer(file);
    const rowExcelData = XLSX.read(data, { type: 'array' });
    const workbook: any = await convertBufferToWorkSheet(data);
    // 一度でも保存が行われたかどうかのフラグ
    let succeedSave = false;

    // まずXlsxPopulate出力用のデータを作成する
    for (const pageInfo of outputExcelPageNames) {
      // 転記先のシートが存在しない場合、読み飛ばす
      if (!rowExcelData.Sheets[pageInfo[0][0]]) continue;
      const sheetName: string = pageInfo[0][0];
      const output: ExcelOutputResult = {};
      // キー列名_列名のオブジェクトの配列、オブジェクトの情報として、セルに入力されているvalueやセルアドレスが含まれている
      orders.forEach((order, index) => {
        // TODO: ひとまずleanpathのカスタム書式で出力できるようにしているが長瀬にも対応必須
        const userSettingRowInfo = this._getExcelRowInfo(pageInfo[1]);
        if (!userSettingRowInfo) return new Error('エクセル情報の取得に失敗しました');

        Object.keys(userSettingRowInfo).forEach((key) => {
          if (userSettingRowInfo[key].isDisplay) {
            const cellCol = userSettingRowInfo[key].displayCell.replace(/[^a-z]/gi, '');
            const cellRow = parseInt(userSettingRowInfo[key].displayCell.replace(/[^0-9]/g, ''), 10) + index;
            const outputCellNumber = cellCol + cellRow;
            const inputValue = this._getOutputOrderValue(order, key);
            output[outputCellNumber] = {
              address: outputCellNumber,
              value: inputValue,
              prevValue: '',
              // ここ以下は変更履歴作成に使用するために追加
              columnName: userSettingRowInfo[key].colName,
              transactionInfo: undefined,
              isDuplicatedBusiness: false,
            };
          }
        });
      });
      const worksheet = workbook.sheet(sheetName);
      for (const key of Object.keys(output)) {
        // セル番号をコードで扱える形に変換して、列情報を取得
        const colIndex = XLSX.utils.decode_cell(key).c;
        // I列に書き込む場合、セル内で改行できるようにスタイルの変更を行う
        if (colIndex === 8) {
          worksheet.cell(key).style({
            wrapText: true,
          });
        }
        worksheet.cell(key)?.value(output[key].value ?? '-');
      }
      const wbout = await workbook.outputAsync();
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      // 日付情報の作成
      saveAs(blob, `${t('確定発注出力', { date: DateService.getTodayString() })}.xlsx`);
      succeedSave = true;
    }
    if (!succeedSave) return new Error('出力に失敗しました。');
  };

  private static _getExcelRowInfo = (pageOption: ExcelParseOption): IUserCustomExcelInfoGroup | undefined => {
    switch (pageOption.sheetName) {
      case '確定発注出力':
        return defaultFormatFirmOrderExcelInfo;
      case '取込転記用':
        // 長瀬書式
        return sampleCustomFirmOrderExcelInfo;
      default:
        return undefined;
    }
  };

  private static _getOutputOrderValue = (order: OutputOrderData, key: string): string => {
    switch (key) {
      case 'orderId':
        // 発注ID (LP発注番号)
        return order.orderId ?? '-';
      case 'createdOrderDate':
        // 発注日
        return order[key] ? convertIDateToDateFormat(convertUnixTimeToIDate(order[key]), myPropertyPreferences.outputDateFormat) : '-';
      case 'desiredDeliveryDate':
        // 希望納期
        return convertIDateToDateFormat(convertUnixTimeToIDate(order[key]), myPropertyPreferences.outputDateFormat);
      case 'estimatedTimeOfArrival':
        // 納入日
        return convertIDateToDateFormat(convertUnixTimeToIDate(order[key]), myPropertyPreferences.outputDateFormat);
      case 'expectedShippingDate':
        // 出荷予定日（= 希望納期）
        const time = order['desiredDeliveryDate'];
        if (!time || typeof time !== 'number') return '-';

        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date(time)), myPropertyPreferences.outputDateFormat);
      case 'receiveOrderDate':
        // 受注日（出力を行う当日の日付）
        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date()), myPropertyPreferences.outputDateFormat);
      case 'createDate':
        // 作成日（出力を行う当日の日付）
        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date()), myPropertyPreferences.outputDateFormat);
      case 'businessMemo':
        // 顧客台帳ID
        return order.userCustomParameters?.['顧客台帳ID'] ?? '-';
      case 'quantity':
        // 個数
        if (order.orderAmount === undefined || order.orderAmount === 0 || order.package === undefined || order.package === 0) {
          return '-';
        }
        // 個数 = 数量 / 容量
        return order.orderAmount.divideIntegersToFixed(order.package);
      case 'buyOrderNumber':
        // 買注文NO（= 発注ID）
        return order.orderIssuedNumber ?? '-';
      case 'aggregateBusinessResponsibleUsers':
        // 仕入担当者
        return order.aggregateBusinessResponsibleUsers?.map((user) => user.name).join(', ') || '-';
      case 'orderNote':
        // 発注メモ
        return order.orderNote ?? '-';
      default:
        return order[key] ?? '-';
    }
  };

  /* TODO:リファクタ対象 */
  /**
   * 出力エクセルの作成を行う (仕入先内示)
   * @param file ファイル情報
   * @param businessIds 取引ID
   * @param yearMonth 年月を表す文字列
   * @param monthRange nヶ月先といった範囲
   */
  public static createSupplierAnnounceExcelForExport = async (
    file: File,
    businessIds: string[],
    yearMonth?: string,
    monthRange?: number
  ): Promise<Array<Blob>> => {
    if (!file) return [];
    const f = file;

    // オプションの取得
    const outputExcelPageNames: [[string, string], ExportExcelParseOption][] = addFileNameToOutputExcelOption(
      f.name,
      defaultExportExcelPageNames
    );

    // xlsmの方で一旦解析をしておいてそれを用いてXlsxPopulateで貼り付ける方針で
    const data = await fm.convertFileToBuffer(f);
    const rowExcelData = XLSX.read(data, { type: 'array' });
    let workbook: any = await fm.convertBufferToWorkSheet(data);

    // シート名ごとに処理を行う
    for (const pageInfo of outputExcelPageNames) {
      if (!rowExcelData.Sheets[pageInfo[0][0]]) continue;
      const sheetName: string = pageInfo[0][0];
      // 列名更新前のsheet情報の作成
      const worksheet = workbook.sheet(sheetName);
      // 列名の更新
      // TODO: 仕入先内示出力限定もしくは列名更新機能を選択性にした方が良いかも
      updateExportExcelColumnName(worksheet, pageInfo[1], yearMonth, monthRange);
      const updatedBlob = await workbook.outputAsync();
      const updatedArrayBuffer = await fm.blobToArrayBuffer(updatedBlob);
      const updatedRowExcelData = XLSX.read(updatedArrayBuffer, { type: 'array' });
      const sheet = updatedRowExcelData.Sheets[sheetName];
      // パースを行う
      const outputExcelRowInfo: ExcelOutputRow[] = TranslateOutputFileManager.parseOutputExcelObjects(
        sheet,
        pageInfo[0][1],
        '仕入先内示出力',
        pageInfo[1]
      );
      // ワークブックデータの再定義
      const updatedWorkbook: any = await fm.convertBufferToWorkSheet(updatedBlob);
      // ワークシートデータの再定義
      const updatedWorksheet = updatedWorkbook.sheet(sheetName);
      // splitする
      const splitExcelData = await ExportFileManager.splitExportExcel(outputExcelRowInfo, businessIds, updatedWorksheet);
      // エクセルのworkbook情報に転写する
      for (const k of Object.keys(splitExcelData)) {
        if (k !== 'undefined') {
          const translateValue = splitExcelData[k].value;
          updatedWorksheet.cell(k).value(translateValue);
          // 塗りつぶしを設定
          updatedWorksheet.cell(k).style('fill', splitExcelData[k].colorInfo);
          // ボーダーを設定
          updatedWorksheet.cell(k).style('border', splitExcelData[k].borderInfo);
        }
      }
      workbook = updatedWorkbook;
    }

    // ファイル出力
    const wbout = await workbook.outputAsync();
    const blob = new Blob([wbout], { type: 'application/octet-stream' });

    return [blob];
  };

  /**
   * 取引の各種情報から出力エクセルに必要な情報を取り出して出力用のオブジェクトにする
   * @param excelData エクセルのデータ
   * @param businessIds 取引ID
   * @param workSheetInfo worksheet情報
   * @returns どこのセルに何の値を入れるか
   */
  public static splitExportExcel = async (
    excelData: ExcelOutputRow[],
    businessIds: string[],
    workSheetInfo: any // TODO: typesどっかから持って来れないか検討する
  ): Promise<ExcelExportResult> => {
    const output: ExcelExportResult = {};
    const businessData: IBusiness[] = simpleCopy(await getBusinessManager().getById(businessIds));
    businessData.forEach((business, index) => {
      createExportSupplierForecastInfo(business, output, excelData[0], index, workSheetInfo);
    });
    return output;
  };
  /* TODO:リファクタ対象 終 */
}

/**
 * 発注情報を確定発注に出力する用に取引情報から取得・加工する
 * @param key - IArrivalのkey
 * @param recommend - 発注情報
 * @returns エクセル出力用の文字列
 */
const outputDateInfoForRecommend = (key: string, recommend?: IArrival): string => {
  if (recommend === undefined) return '希望納期なし';
  // TODO: モーダル出すなどのユーザーに通知する機能の作成が必要だが、一旦データ入力させないようにしている
  if (recommend.year === -1 && recommend.month === -1 && recommend.day === -1) return '希望納期なし';
  const date = new Date();
  // TODO: 分岐条件内示決め打ちなのどうにかしたいけど、とりあえずで置いてます。時間ある時にやる
  const fmt = myPropertyPreferences.outputDateFormat;
  if (key === 'createDate' || key === 'receiveOrderDate') {
    const dateObj: IDate = DateService.convertYearMonthDayToIDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return convertIDateToDateFormat(dateObj, fmt);
  } else if (key === 'expectedShippingDate') {
    const dateObj: IDate = DateService.convertYearMonthDayToIDate(
      recommend['crossDate']?.year ?? -1,
      recommend['crossDate']?.month ?? -1,
      (recommend['crossDate']?.day ?? -1) - 1
    );
    return convertIDateToDateFormat(dateObj, fmt);
  } else if (key === 'crossDate') {
    const dateObj: IDate = DateService.convertYearMonthDayToIDate(
      recommend[key]?.year ?? -1,
      recommend[key]?.month ?? -1,
      recommend[key]?.day ?? -1
    );
    return convertIDateToDateFormat(dateObj, fmt);
  } else if (key === 'fixOrderDate') {
    const dateObj: IDate = DateService.convertYearMonthDayToIDate(recommend.year, recommend.month, recommend.day);
    return convertIDateToDateFormat(dateObj, fmt);
  }
  const dateObj: IDate = DateService.convertYearMonthDayToIDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  return convertIDateToDateFormat(dateObj, fmt);
};

export { ExportFileManager as exportFm };

/* TODO:リファクタ対象 */
/**
 * エクセル出力を行うために、必要な情報を持つオブジェクトの作成を行う
 * @param business 取引情報
 * @param output エクセル出力用のオブジェクト
 * @param rowInfo 入力されたエクセルの行ごとの情報
 * @param addRowCount 追加される行数
 * @param workSheetInfo worksheetの情報
 */
const createExportSupplierForecastInfo = (
  business: IBusiness,
  output: ExcelExportResult,
  rowInfo: ExcelOutputRow,
  addRowCount: number,
  workSheetInfo: any // TODO: typesどっかから持って来れないか検討する
) => {
  // 列ごとに見ていく
  Object.keys(rowInfo).forEach((key) => {
    // 一応、セル番号が文字列かを見る
    if (typeof rowInfo[key].address === 'string') {
      // セル番号をコードで扱える形に変換する
      const cellAddress = XLSX.utils.decode_cell(rowInfo[key].address as string);
      // 取引の数分、下に行を進める
      const writeToAddress = XLSX.utils.encode_cell({ c: cellAddress.c, r: cellAddress.r + addRowCount });
      const cellNumber = String(writeToAddress); // セル番号
      // セルのstyle情報の取得を行う
      // エクセル番号、セルに入れる値を持つオブジェクトの初期化
      if (!output[writeToAddress]) {
        output[writeToAddress] = {
          value: undefined,
          address: undefined,
          colorInfo: undefined,
          borderInfo: undefined,
        };
      }
      // 出力先セル番号の取得
      const propertyValue = createSupplierForecastValueFetchFromProperty(business, key);
      // 出力情報の登録
      output[cellNumber].address = writeToAddress;
      output[cellNumber].value = propertyValue;
      // style情報の登録を行う
      // style情報は取引情報が入っている一番最初の行のstyleを下の行にコピーする形にしている
      if (addRowCount === 0) {
        // sheetJSでの取得は(pro版じゃないと)できないので、xlsx-populateを用いて行う必要がある
        const cellFillInfo: object = workSheetInfo.cell(cellNumber).style('fill');
        const cellBorderInfo: object = workSheetInfo.cell(cellNumber).style('border');
        output[cellNumber].colorInfo = cellFillInfo;
        output[cellNumber].borderInfo = cellBorderInfo;
      } else {
        output[cellNumber].colorInfo = workSheetInfo.cell(rowInfo[key].address as string).style('fill');
        output[cellNumber].borderInfo = workSheetInfo.cell(rowInfo[key].address as string).style('border');
      }
    }
  });
};

/**
 * 列名からどのプロパティを取得するか、対応表から選択する
 * @param key 列名(columnNameWithBusinessKeyRepresentMap)のkey
 */
const selectProperty = (key: string): string => {
  // 対応表示から取引情報のプロパティを取得する
  if (columnNameWithBusinessKeyRepresentMap[key]) {
    return columnNameWithBusinessKeyRepresentMap[key];
  } else {
    // 対応表に存在しない場合は空文字を返す
    return '';
  }
};

// 「内示」または「販売実績」の列か判定する
const isAnnounceOrSalesResultProperty = (key: string): boolean => {
  return key.includes('月内示') || key.includes('月販売実績');
};

/**
 * 取引の各種情報から出力エクセルに必要な情報を取り出す
 * @param business 取引情報
 * @param columnName 列名
 * @returns 取引情報から取り出した値
 */
const createSupplierForecastValueFetchFromProperty = (business: IBusiness, columnName: string): string | number => {
  // 内示または販売実績の列かを確認する
  if (isAnnounceOrSalesResultProperty(columnName)) {
    // 「内示」、「販売実績」だった場合
    const elementArray: ISalesResult[] | IAnnounce[] = business[selectProperty(columnName.split('月')[1])];
    if (!elementArray) return '';
    // 年月情報
    const yearMonth: string = columnName.split('月')[0];
    const year = Number(yearMonth.split('_')[0]);
    const month = Number(yearMonth.split('_')[1]);
    // 内示または販売実績かつ指定したプロパティが存在することを確認する
    if (elementArray) {
      const foundElement = elementArray.find((element) => element.year === year && element.month === month);
      if (foundElement) {
        // 数量情報を返す
        return foundElement.amount ?? 0;
      }
    }
  } else {
    // 「内示」、「販売実績」ではない場合
    if (business[selectProperty(columnName)]) {
      return business[selectProperty(columnName)];
    }
  }
  // 全ての条件に一致しない場合空文字を返す
  return '';
};

/**
 * 出力用エクセルファイルの列名更新、スタイル更新を行う
 * 出力する期間
 * @param workSheetInfo worksheet情報
 * @param option エクセルを読むためのオプションをまとめたもの
 * @param yearMonth 年月を表す文字列
 * @param outputPeriod nヶ月先を表す情報
 * @returns workSheetの情報 TODO: これの型なんとかする (sheetJSのやつ)
 */
const updateExportExcelColumnName = (workSheetInfo: any, option: ExportExcelParseOption, yearMonth?: string, outputPeriod = 3): any => {
  // デフォルト値の設定
  if (!yearMonth) {
    const nowDate = new Date();
    yearMonth = String(nowDate.getFullYear()) + '_' + String(nowDate.getMonth() + 1);
  }

  // 更新開始セル(年月データが入っているセル)のアドレスを取る
  const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
  const valueStartAddress = XLSX.utils.decode_cell(option.valueStartCell);
  const separateCount = option.separateYmd.separateCount;
  const rangeVal = outputPeriod + 1; // 当月からnヶ月分先までの範囲

  // 年月データが入っている列(内示・販売実績数量がある列)を対象に列名を決定する
  for (let i = 0; i < rangeVal; i++) {
    const updateYearMonthValue = DateService.updateYearMonth(Number(yearMonth.split('_')[0]), Number(yearMonth.split('_')[1]), i); // 年月情報を設定した値に更新する
    // WorkBook内の情報を更新する
    for (let r = 0; r < separateCount; r++) {
      // どこのセル番号を見るか決める
      // 月数*何ヶ月先の情報か？+同じ月情報の列数で更新したい範囲分のセル番号を左側から順に更新をかけるようにしている
      const lookingCoordinate = separateCount * i + r;
      const yearAdr = XLSX.utils.encode_cell({ c: startAddress.c + lookingCoordinate, r: startAddress.r }); // 年データが入っているセル
      const colNameAdr = XLSX.utils.encode_cell({ c: startAddress.c + lookingCoordinate, r: startAddress.r + 1 }); // 列名が入っているセル
      const valueAdr = XLSX.utils.encode_cell({ c: valueStartAddress.c + lookingCoordinate, r: valueStartAddress.r }); // 値が入るセル

      // 始点のセルのスタイル情報を取得
      const startCellColor = workSheetInfo.cell(option.separateYmd.startCell).style('fill');
      const startCellBorder = workSheetInfo.cell(option.separateYmd.startCell).style('border');
      const startCellBold = workSheetInfo.cell(option.separateYmd.startCell).style('bold');
      const valueStartCellColor = workSheetInfo.cell(option.valueStartCell).style('fill');
      const valueStartCellBorder = workSheetInfo.cell(option.valueStartCell).style('border');

      // 年情報の更新
      workSheetInfo.cell(yearAdr).value(updateYearMonthValue.updateYear);
      workSheetInfo.cell(yearAdr).style('fill', startCellColor);
      workSheetInfo.cell(yearAdr).style('border', startCellBorder);
      workSheetInfo.cell(yearAdr).style('bold', startCellBold);

      // 値が入るセルのスタイルのみ更新
      workSheetInfo.cell(valueAdr).style('fill', valueStartCellColor);
      workSheetInfo.cell(valueAdr).style('border', valueStartCellBorder);
      // 月情報の取得
      const updateMonthName = String(updateYearMonthValue.updateMonth) + '月';
      // 月情報の更新
      // 新たな列を生成する必要があるため、空セルを読んだ際の対応分けをしている
      if (workSheetInfo.cell(colNameAdr).value()) {
        if (workSheetInfo.cell(colNameAdr).value().includes('月')) {
          const updateColName = updateMonthName + workSheetInfo.cell(colNameAdr).value().split('月')[1];
          workSheetInfo.cell(colNameAdr).value(updateColName);
        }
      } else {
        // 新規追加列に年月情報が必要かどうか判定を行う
        if (option.addColumnName[r].isUpdateCalender) {
          // ExportExcelParseOptionから追加する列名の情報を取ってくる
          const addColName = updateMonthName + option.addColumnName[r].colName;
          workSheetInfo.cell(colNameAdr).value(addColName);
          workSheetInfo.cell(colNameAdr).style('fill', startCellColor);
          workSheetInfo.cell(colNameAdr).style('border', startCellBorder);
          workSheetInfo.cell(yearAdr).style('bold', startCellBold);
        } else {
          // 新規列に年月情報がいらない場合、文字列の結合処理を行わない
          workSheetInfo.cell(colNameAdr).value(option.addColumnName[r].colName);
          workSheetInfo.cell(colNameAdr).style('fill', startCellColor);
          workSheetInfo.cell(colNameAdr).style('border', startCellBorder);
          workSheetInfo.cell(yearAdr).style('bold', startCellBold);
        }
      }
    }
  }
  return workSheetInfo;
};
/* TODO:リファクタ対象 終*/
